import html2canvas from 'html2canvas';
import {
  SEAT_CELL_PX,
  SEAT_CHIP_PX,
  SEAT_CHIP_BORDER_RADIUS_PX,
  seatBoardPx
} from './seatBoardLayout';

type SeatingSeat = {
  studentId: string | null;
  studentName: string | null;
  isDisabled: boolean;
  seatTax?: number | null;
};

type SeatingArrangement = {
  settings: {
    rows: number;
    cols: number;
    isPairedSeating: boolean;
    boardPosition: 'top' | 'bottom';
  };
  seats: Record<string, SeatingSeat>;
};

type StudentLike = {
  uid: string;
  number?: number;
  name: string;
};

type TeacherLike = {
  homeroomClass?: { grade: number; class: number };
  name?: string;
};

export const printSeatingArrangement = async ({
  seatingArrangement,
  students,
  teacher,
  boardIconElement,
  title
}: {
  seatingArrangement: SeatingArrangement;
  students: StudentLike[];
  teacher: TeacherLike;
  boardIconElement: HTMLElement | null;
  /** 미지정 시 교사 담임 학년·반 기준으로 "~학년 ~반 자리배치도" */
  title?: string;
}) => {
  if (!seatingArrangement) {
    alert('자리배치표가 없습니다.');
    return;
  }

  const displayTitle =
    title?.trim() ||
    (teacher.homeroomClass
      ? `${teacher.homeroomClass.grade}학년 ${teacher.homeroomClass.class}반 자리배치도`
      : '자리배치도');

  try {
    const captureBoardIcon = async () => {
      if (!boardIconElement) return null;
      const canvas = await html2canvas(boardIconElement, {
        backgroundColor: '#ffffff',
        scale: Math.max(window.devicePixelRatio || 1, 2),
        useCORS: true,
        logging: false
      });
      return canvas.toDataURL('image/png');
    };

    const boardIconDataUrl = await captureBoardIcon();

    const rows = seatingArrangement.settings.rows;
    const cols = seatingArrangement.settings.cols;

    const escapeHtml = (str: string) =>
      str
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');

    const findStudentName = (studentId: string | null, seatStudentName: string | null) => {
      if (!studentId) return seatStudentName ?? '';
      const sid = String(studentId).trim();
      const matched = students.find(s => String(s.uid).trim() === sid);
      return matched?.name ?? seatStudentName ?? '';
    };

    const findStudentById = (studentId: string | null) => {
      if (!studentId) return null;
      const sid = String(studentId).trim();
      return students.find(s => String(s.uid).trim() === sid) ?? null;
    };

    const getStudentNumberLabel = (student: StudentLike | null): string => {
      if (!student) return '';
      const raw = student.number as unknown;
      const num =
        raw === null || raw === undefined || raw === ''
          ? NaN
          : typeof raw === 'number'
            ? raw
            : Number(raw);
      if (Number.isFinite(num) && num > 0) {
        return `${Math.floor(num)}번`;
      }
      return '';
    };

    const seatCircleHtml = (seatRow: number, col: number) => {
      const seatKey = `${seatRow}-${col}`;
      const seat = seatingArrangement.seats[seatKey];
      const studentId = seat?.studentId ?? null;
      const isDisabled = seat?.isDisabled ?? false;
      const studentName = findStudentName(studentId, seat?.studentName ?? null);
      const hasAssignedStudent =
        studentId != null || (studentName.trim().length > 0);

      // 빈 자리(이름/학생 없음): 출력에서는 원형 칩 없이 빈 칸만 유지
      if (!isDisabled && !hasAssignedStudent) {
        return '';
      }

      const seatLabel = `${String.fromCharCode(65 + seatRow)}${String(col + 1).padStart(2, '0')}`;
      const matchedStudent = findStudentById(studentId);
      const numberLabel = getStudentNumberLabel(matchedStudent);
      const topLabelText =
        hasAssignedStudent && numberLabel
          ? numberLabel
          : seatLabel;

      const bgColor = isDisabled
        ? '#EDEDED'
        : studentId
          ? '#FFF1E7'
          : '#F8F8F8';

      const textColor = isDisabled ? '#BABABA' : '#4C4C4C';

      const nameHtml =
        hasAssignedStudent && studentName
          ? `<div class="seat-name" style="color: ${textColor};">${escapeHtml(studentName)}</div>`
          : '';

      const disabledHtml = isDisabled
        ? `<div class="seat-disabled" style="color: ${textColor};">비활성화</div>`
        : '';

      const taxRaw = seat?.seatTax;
      const seatTaxHtml =
        taxRaw != null && Number.isFinite(Number(taxRaw))
          ? `<div class="seat-tax" style="color:#FF6F0F;font-size:10px;font-weight:600;margin-top:2px;">자리세 ${escapeHtml(String(Math.floor(Number(taxRaw))))}</div>`
          : '';

      return `
        <div class="seat-circle ${isDisabled ? 'seat-disabled-circle' : studentId ? 'seat-active-circle' : 'seat-empty-circle'}" style="width:${SEAT_CHIP_PX}px;height:${SEAT_CHIP_PX}px;border-radius:${SEAT_CHIP_BORDER_RADIUS_PX}px;background:${bgColor};color:${textColor};">
          <div class="seat-label" style="color:${textColor};">${escapeHtml(topLabelText)}</div>
          ${nameHtml}
          ${disabledHtml}
          ${seatTaxHtml}
        </div>
      `;
    };

    // 관람자 기준: 아래가 A열(원래 row=0)을 보이려면 출력용 row를 뒤집는다
    // 열: 오른쪽이 01, 왼쪽으로 02, 03… (데이터 col 0이 화면 오른쪽)
    let gridHtml = '';
    for (let printRow = 0; printRow < rows; printRow++) {
      const seatRow = rows - 1 - printRow;
      gridHtml += `<div class="grid-row" style="height:${SEAT_CELL_PX}px;">`;
      for (let col = cols - 1; col >= 0; col--) {
        gridHtml += `<div class="seat-cell" style="width:${SEAT_CELL_PX}px;height:${SEAT_CELL_PX}px;">${seatCircleHtml(seatRow, col)}</div>`;
      }
      gridHtml += `</div>`;
    }

    const boardBlockHtml = boardIconDataUrl
      ? `
        <div class="board-block">
          <img class="board-icon" src="${boardIconDataUrl}" alt="" />
          <div class="board-label">칠판</div>
        </div>
      `
      : `
        <div class="board-block">
          <div class="board-label">칠판</div>
        </div>
      `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('팝업 차단으로 인해 출력 창을 열 수 없습니다.');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(displayTitle)}</title>
          <style>
            @page { margin: 4mm; size: landscape; }
            /* 인쇄/미리보기에서 세로·가로 중앙: 높이 체인 + grid */
            html {
              height: 100%;
            }
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              box-sizing: border-box;
            }
            body {
              background: white;
              font-family: 'Pretendard', sans-serif;
              height: 100%;
              min-height: 100%;
              min-height: 100vh;
              min-height: 100dvh;
              display: flex;
              align-items: center;
              justify-content: center;
              /* scale(1) 초과 시 상·하단이 잘리지 않도록 */
              overflow: auto;
            }
            .scale-root {
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: visible;
              box-sizing: border-box;
              padding: 8px;
            }
            .scale-inner {
              transform-origin: center center;
              width: max-content;
              max-width: 100%;
              margin: 0;
              padding: 8px 12px;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: ${seatBoardPx(36)}px;
            }
            .title {
              text-align: center;
              width: 100%;
              font-size: ${seatBoardPx(30)}px;
              font-weight: 900;
              margin: 0;
            }
            .grid { width: fit-content; margin: 0 auto; }
            .grid-row { display:flex; flex-direction: row; gap: 0; }
            .seat-cell { display:flex; justify-content:center; align-items:center; }
            /* 앱과 동일: 테두리·그림자 없이 배경색만 (인쇄 시 색 유지) */
            .seat-circle {
              display:flex;
              flex-direction:column;
              justify-content:center;
              align-items:center;
              box-sizing:border-box;
              border: none;
              box-shadow: none;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .seat-active-circle,
            .seat-empty-circle,
            .seat-disabled-circle {
              border: none;
              box-shadow: none;
            }
            .seat-label {
              font-size: ${seatBoardPx(14)}px;
              font-weight: 600;
              line-height: ${seatBoardPx(18)}px;
              text-align: center;
              min-width: ${seatBoardPx(44)}px;
              font-variant-numeric: tabular-nums;
              font-feature-settings: "tnum" 1;
              display: inline-block;
              box-sizing: border-box;
            }
            .seat-name { font-size: ${seatBoardPx(13)}px; font-weight: 500; line-height: ${seatBoardPx(17)}px; margin-top: 1px; text-align:center; max-width: ${seatBoardPx(68)}px; word-break: keep-all; }
            .seat-disabled { font-size: ${seatBoardPx(9)}px; font-weight: 400; line-height: ${seatBoardPx(12)}px; margin-top: 2px; }
            .board-block { width: 100%; max-width: 100%; display:flex; flex-direction:column; align-items:center; justify-content:center; margin-top: 0; }
            .board-icon { width: 100%; max-width: 100%; height: auto; display:block; transform: scaleY(-1); }
            .board-label { text-align:center; font-size: ${seatBoardPx(28)}px; font-weight: 900; margin-top: ${seatBoardPx(12)}px; color: #4C4C4C; }
            @media print {
              html, body {
                height: 100%;
                min-height: 100%;
              }
              body {
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
              }
            }
          </style>
        </head>
        <body>
          <div class="scale-root">
            <div class="scale-inner" id="print-scale-inner">
              <div class="title">${escapeHtml(displayTitle)}</div>
              <div class="grid">
                ${gridHtml}
              </div>
              ${boardBlockHtml}
            </div>
          </div>
          <script>
            (function () {
              function fitPageFill() {
                var inner = document.getElementById('print-scale-inner');
                var root = document.querySelector('.scale-root');
                if (!inner) return;
                var pad = 16;
                inner.style.transform = 'none';
                inner.style.zoom = '';
                if (root) {
                  root.style.height = '';
                  root.style.minHeight = '';
                  root.style.overflow = '';
                }
                var vw = window.innerWidth || document.documentElement.clientWidth;
                var vh = window.innerHeight || document.documentElement.clientHeight;
                var iw = inner.offsetWidth;
                var ih = inner.offsetHeight;
                if (iw < 1 || ih < 1) return;
                var scaleW = (vw - pad * 2) / iw;
                var scaleH = (vh - pad * 2) / ih;
                /* 한 페이지에 맞출 때만 '축소'. 확대(>1)는 하지 않음 → 제목·전체가 잘리는 현상 방지 */
                var scale = Math.min(scaleW, scaleH, 1);
                if (scale > 0 && isFinite(scale)) {
                  /* transform은 레이아웃 높이를 줄이지 않아 인쇄 시 2페이지로 나뉘며 잘릴 수 있음 → zoom 우선 */
                  if (typeof inner.style.zoom !== 'undefined' && 'zoom' in inner.style) {
                    inner.style.zoom = String(scale);
                  } else if (root) {
                    inner.style.transform = 'scale(' + scale + ')';
                    inner.style.transformOrigin = 'center center';
                    var scaledH = ih * scale;
                    root.style.height = scaledH + 'px';
                    root.style.minHeight = scaledH + 'px';
                    root.style.overflow = 'hidden';
                  } else {
                    inner.style.transform = 'scale(' + scale + ')';
                  }
                }
              }
              window.addEventListener('beforeprint', fitPageFill);
              window.addEventListener('resize', fitPageFill);
              window.onload = function () {
                fitPageFill();
                requestAnimationFrame(function () {
                  fitPageFill();
                  setTimeout(function () {
                    fitPageFill();
                    window.focus();
                    window.print();
                    window.close();
                  }, 200);
                });
              };
            })();
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  } catch (error) {
    console.error('자리배치표 출력 중 오류:', error);
    alert('자리배치표 출력 중 오류가 발생했습니다.');
  }
};